import { ColumnsType } from '@setvi/shared/types';
import { useMemo } from 'react';
import { Box } from '@material-ui/core';
import { SText } from '@setvi/shared/components';
import { TextColor } from '@setvi/shared/enums';
import { Stock } from 'pages/products/components/stock';
import { Product } from '@setvi/shared/interfaces';
import {
  Profit,
  Cost,
  Price
} from 'pages/products/components/products-list/useProductColumns/contents';
import { ProductLabel, ProductName } from 'componentsV2';
import { SimilarProductsProps } from '../..';

export const useSimilarProductsColumns = ({
  selectedCustomer,
  openProductDetails
}: Pick<SimilarProductsProps, 'selectedCustomer' | 'openProductDetails'>) => {
  const columns: ColumnsType = useMemo(
    () => [
      {
        id: 'productName',
        label: 'Product Name',
        maxWidth: '50%',
        minWidth: '320px',
        cellContent: (product: Product) => <ProductName product={product} />
      },
      {
        id: 'manufacturer',
        maxWidth: '20%',
        minWidth: '180px',
        label: 'Manufacturer & SKU',
        cellContent: ({
          vendor,
          manufacturerSku,
          isPrivateLabel,
          vendorRanking
        }: Product) => (
          <Box display="flex" gridGap="4px">
            <Box display="flex" flexDirection="column" gridGap="4px">
              <SText lineHeight="24px" title={vendor?.name || '-'} />
              <SText
                title={manufacturerSku || '-'}
                fontColor={TextColor.SlateGrey}
                lineHeight="20px"
                size="sm"
              />
            </Box>

            <Box display="flex" gridGap="2px">
              {isPrivateLabel && <ProductLabel />}
              {vendorRanking > 0 && <ProductLabel type="strategic-vendor" />}
            </Box>
          </Box>
        )
      },
      {
        id: 'stock',
        width: '9%',
        maxWidth: '140px',
        minWidth: '140px',
        label: 'Stock',
        cellContent: (product: Product) => {
          const leadTime =
            // @ts-ignore
            product?.custom?.ship_lead_time__business_days?.value;
          return (
            <Box display="flex" flexDirection="column" gridGap="4px">
              <Stock
                inventory={product?.inventory}
                isDropship={product?.dropship}
                selectedCustomer={selectedCustomer}
                onClick={() => openProductDetails(product)}
              />
              {leadTime && (
                <SText size="sm" lineHeight="20px" title={leadTime} />
              )}
            </Box>
          );
        }
      },
      ...(selectedCustomer
        ? [
            {
              id: 'sell',
              width: '7%',
              maxWidth: '100px',
              minWidth: '80px',
              label: 'Sell',
              cellContent: ({ price }: Product) => <Price value={price} />
            }
          ]
        : []),
      {
        id: 'cost',
        width: '7%',
        maxWidth: '100px',
        minWidth: '80px',
        label: 'Cost',
        cellContent: ({ cost }: Product) => <Cost value={cost} />
      },
      ...(selectedCustomer
        ? [
            {
              id: 'GP',
              width: '7%',
              maxWidth: '100px',
              minWidth: '80px',
              label: 'GP',
              cellContent: ({ price, cost }: Product) => (
                <Profit price={price} cost={cost} />
              )
            }
          ]
        : [])
    ],
    [openProductDetails, selectedCustomer]
  );

  return { columns };
};
